/**
 * The arithmetic and words of the billing page's add-on offers (TOADO-660).
 * Pure, so each rule is tested without rendering. What may be bought is the
 * server's decision (`addons.offers`); this only lays it out.
 */

export type AddonKey = 'narration_boost' | 'recording_boost' | 'video_history' | 'narration_pack' | 'recording_pack';
export type Meter = 'recording' | 'narration' | 'storage';

export interface AddonOffer {
  addon: AddonKey;
  kind: 'boost' | 'pack';
  minutesPerUnit: number | null;
  centsPerUnit: number;
  perSeat: boolean;
}

export interface AddonsState {
  offers: AddonOffer[];
  /** What renews on the 1st, by add-on. */
  renewing: Partial<Record<'narration_boost' | 'recording_boost' | 'video_history', number>>;
  status: string | null;
  packs: Array<{ addon: AddonKey; minutesLeft: number; expiresAt: string }>;
}

/** The share of a meter at which the bar turns amber and the run-out day is shown. */
export const WARN_SHARE = 0.8;

export function meterState(used: number, limit: number | null): 'ok' | 'warn' | 'full' | 'unlimited' {
  if (limit === null) return 'unlimited';
  if (used >= limit) return 'full';
  return limit > 0 && used / limit >= WARN_SHARE ? 'warn' : 'ok';
}

/**
 * The day this month's pace runs a meter out, or null when it will not run
 * out this month (or there is nothing to go on yet). UTC calendar month, as
 * the server counts it.
 */
export function runOutDay(used: number, limit: number | null, now: Date = new Date()): Date | null {
  if (limit === null || used <= 0 || used >= limit) return null;
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
  const elapsed = Math.max(now.getTime() - start, 60 * 60 * 1000);
  const at = start + (limit / used) * elapsed;
  return at < end ? new Date(at) : null;
}

const METER_ADDONS: Record<Exclude<Meter, 'storage'>, { pack: AddonKey; boost: AddonKey }> = {
  recording: { pack: 'recording_pack', boost: 'recording_boost' },
  narration: { pack: 'narration_pack', boost: 'narration_boost' },
};

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

export interface GetMoreChoice {
  key: string;
  label: string;
  detail: string;
  /** An add-on to buy, or a plan to move to. */
  action: { kind: 'addon'; addon: AddonKey } | { kind: 'plan'; plan: 'personal' | 'team' | 'business' } | { kind: 'contact' };
}

/**
 * What "Get more" offers for one meter, in order: this month's pack, the
 * same every month, then the next plan, and when there is nothing to buy,
 * contact. Only what the server offers.
 */
export function getMoreChoices(meter: Meter, addons: AddonsState | undefined, plan: string, businessOnSale = false): GetMoreChoice[] {
  const choices: GetMoreChoice[] = [];
  if (meter !== 'storage') {
    const { pack, boost } = METER_ADDONS[meter];
    const offer = (key: AddonKey) => addons?.offers.find((o) => o.addon === key);
    const packOffer = offer(pack);
    if (packOffer?.minutesPerUnit) {
      choices.push({
        key: pack,
        label: `${packOffer.minutesPerUnit} more minutes, once`,
        detail: `${dollars(packOffer.centsPerUnit)}. Used after the month's minutes, good for 12 months.`,
        action: { kind: 'addon', addon: pack },
      });
    }
    const boostOffer = offer(boost);
    if (boostOffer?.minutesPerUnit) {
      choices.push({
        key: boost,
        label: `${boostOffer.minutesPerUnit} more minutes, every month`,
        detail: `${dollars(boostOffer.centsPerUnit)} a month, starting this month. Renews on the 1st.`,
        action: { kind: 'addon', addon: boost },
      });
    }
  }
  if (plan === 'free') {
    choices.push({ key: 'personal', label: 'Upgrade to Personal', detail: 'More minutes and unlimited tickets.', action: { kind: 'plan', plan: 'personal' } });
    choices.push({ key: 'team', label: 'Upgrade to Team', detail: 'The same per seat, pooled, with teammates.', action: { kind: 'plan', plan: 'team' } });
  } else if (plan === 'personal') {
    choices.push({ key: 'team', label: 'Move to Team', detail: 'Minutes per seat, pooled across the workspace.', action: { kind: 'plan', plan: 'team' } });
  } else if (plan === 'team' && businessOnSale) {
    choices.push({ key: 'business', label: 'Move to Business', detail: 'Twice the minutes per seat and a year of video.', action: { kind: 'plan', plan: 'business' } });
  }
  if (choices.length === 0) {
    choices.push({ key: 'contact', label: 'Contact us', detail: 'We can raise this workspace\'s allowance.', action: { kind: 'contact' } });
  }
  return choices;
}

const ADDON_NAMES: Record<AddonKey, string> = {
  narration_boost: 'Narration minutes, every month',
  recording_boost: 'Recording minutes, every month',
  video_history: 'Video history (1 year)',
  narration_pack: 'Narration minutes pack',
  recording_pack: 'Recording minutes pack',
};

export function addonName(addon: AddonKey): string {
  return ADDON_NAMES[addon];
}
